import React, { useEffect, useState, useRef, useCallback } from 'react'
import { createFormBag, FormBag, FormData, FormErrors, validateFormBag } from 'react-formage'

import { ButtonGroup } from 'components/ui/button'
import { FooterEditEntity } from 'components/ui/footer'
import { Input } from 'components/ui/input'
import { PrimaryNavigationEditEntity } from 'components/ui/primary-navigation/edit-entity'
import { PopOverMenu, PopOverMenuButton } from 'components/ui/pop-over-menu'
import { SecondaryNavigation } from 'components/ui/secondary-navigation'
import { SelectRef } from 'components/ui/select'
import { StatusGuard } from 'components/ui/status-guard'
import { StatusSummary } from 'components/ui/status-summary'

import { sharedActionCreators } from 'containers/shared'
import { connect } from 'containers/store'

import { releaseControlsBindableActionCreators } from 'domains/release-controls/actions'
import { ReleaseControlUpdate } from 'domains/release-controls/components/release-control-update'
import { Media } from 'generated/mos/media'
import { Space, Site } from 'generated/mos/structure'
import { Placement, PositionPlacement, SitePlacement, SpacePlacement } from 'generated/mos/placement'
import { spacesBindableActionCreators } from 'domains/spaces/actions'

import { Page } from 'generated/mos/pagination'
import { EventPointOfInterest } from 'generated/mos/org/getty/audio'
import { GetEventPointOfInterestRequest, ListVisitorTypesRequest } from 'generated/mos/org/getty/audioaccess'
import { UpdateEventPointOfInterestRequest } from 'generated/mos/org/getty/audiomanagement'
import { SetReleaseStatusRequest } from 'generated/mos/releasecontrolsmanagement'
import { ListValidationFailuresRequest } from 'generated/mos/org/getty/validationaccess'
import { getValidationWarning } from 'domains/org/getty/utils/validation'
import { MediaField } from 'domains/media/components/media-field'
import { baseDeepLinkDomain, gettyCenterSiteId, gettyVillaSiteId } from 'helpers/deep-links'

import { getAlternateFieldValues, LinkableValue, getResolvedValue } from 'helpers/linkables'
import { pick, areObjectsEqual, areRefValuesEqual } from 'helpers/core'
import { convertEntityToFormValues, writeUsingSchema } from 'helpers/schema'
import { Status, statusSelector } from 'helpers/status'

import { LayoutListOrFormPage } from 'layouts/list-or-form-page'

import { actionCreators, GettyActionCreators } from '../../../actions'
import { GettyStore } from '../../../store'
import { getEventPointOfInterestSubnav } from '../subnav'
import { EventPointOfInterestDelete } from '../event-point-of-interest-delete'
import { eventPointOfInterestSchema } from '../../../schemas/event-point-of-interest'
import { SpacesStore } from '../../../../../spaces/store'
import { visitorTypeHelpText } from '../../visitor-types/helper'

type FormValues = {
  teaserText: string | undefined;
  content: string | undefined;
  additionalInfo: string | undefined;
  deepDiveLabel: string | undefined;
  deepDiveContextLabel: string | undefined;
  deepDiveUrl: string | undefined;
  placement: Space.Ref | undefined;
  tags: string | undefined;
};

type ActionProps =
  Pick<GettyActionCreators, 'gettyListVisitorTypesRequest' | 'gettyGetEventPointOfInterestRequest' | 'gettyUpdateEventPointOfInterestRequest' | 'gettyUpdateEventPointOfInterestReset' | 'gettyListValidationFailuresRequest' | 'gettyListSitesRequest'> &
  Pick<typeof releaseControlsBindableActionCreators, "releaseControlsListReleaseControlsRequest" | "releaseControlsSetReleaseStatusRequest"> &
  Pick<typeof spacesBindableActionCreators, "spacesRequest"> &
  Pick<typeof sharedActionCreators, 'toastNotification'>;

type ConnectedProps = Pick<GettyStore['getty'], 'eventPointOfInterestUpdate' | 'sites' | 'validationFailuresList' | 'visitorTypes'> & Pick<SpacesStore["spaces"], "spacesList">

type DirectProps = {
  readonly historyPush: (id: string) => void;
  readonly projectId: string;
  readonly pointOfInterestId: string;
  readonly moodId: string;
};
type Props = ActionProps & ConnectedProps & DirectProps;

const makePlacementEntity = (spaceRef: Space.Ref): Placement.Entity => {
  return {
    ...Placement.defaults,
    placement: {
      ...SpacePlacement.defaults,
      spaceRef,
    },
  }
}

const getSpacePlacementSpaceRef = (placement: Placement.Entity): Space.Ref | undefined => {
  if (placement) {
    const { spaceRef } = placement.placement as SpacePlacement.Entity
    return Space.mustRef(spaceRef)
  }
  return undefined
}

export const EventPointOfInterestUpdateView = (props: Props) => {
  const {
    gettyGetEventPointOfInterestRequest,
    gettyListSitesRequest,
    gettyUpdateEventPointOfInterestRequest,
    gettyUpdateEventPointOfInterestReset,
    gettyListVisitorTypesRequest,
    historyPush,
    projectId,
    pointOfInterestId,
    eventPointOfInterestUpdate,
    toastNotification,
    moodId,
    validationFailuresList,
    gettyListValidationFailuresRequest,
    releaseControlsListReleaseControlsRequest,
    releaseControlsSetReleaseStatusRequest,
    sites,
    spacesList,
    spacesRequest,
    visitorTypes,
  } = props

  // setup form value variables.
  const [ formBag, setFormBag ] = useState<FormBag<FormValues> | undefined>(undefined)
  const [ audioMedia, setAudioMedia ] = useState<Media.Ref | undefined>(undefined)
  const [ placementOverride, setPlacementOverride ] = useState<LinkableValue>(undefined)
  const [placementOverrideFieldValues, setPlacementOverrideFieldValues] = useState<
    { label: string; value: LinkableValue }[]
  >([]);
  const hasSubmitted = useRef(false)

  const [ deletePointOfInterestModalOpen, setDeletePointOfInterestModalOpen ] = useState(false)
  const [siteId, setSiteId] = useState('');

  // where to return to if the user wants to navigate back.
  // if we came here from within a tour, go back to that tour, otherwise go to all pointsOfInterest.
  const backLocation = `/project/${projectId}/mood/${moodId}`

  const pointOfInterestData = statusSelector.data(eventPointOfInterestUpdate)
  const validationFailuresListData = statusSelector.data(validationFailuresList)

  const spacesListData = statusSelector.data(spacesList)
  const sitesData = statusSelector.data(sites)
  const visitorTypesData = statusSelector.data(visitorTypes)

  const pointOfInterest = pointOfInterestData ? pointOfInterestData.eventPointOfInterest : undefined
  const pointOfInterestRef: EventPointOfInterest.Ref = {
    typename: EventPointOfInterest.refName,
    id: pointOfInterestId,
  }

  const deepLinkUrl = `${baseDeepLinkDomain}?deeplink=site/${siteId}/mood/${moodId}/point-of-interest/${pointOfInterestId}`

  // load initial pointOfInterest data.
  useEffect(() => {
    if (eventPointOfInterestUpdate.status === Status.Idle || (pointOfInterest && EventPointOfInterest.mustRef(pointOfInterest.ref).id !== pointOfInterestId)) {
      gettyGetEventPointOfInterestRequest({
        type: GetEventPointOfInterestRequest.refName,
        eventPointOfInterestRef: pointOfInterestRef
      })
      gettyListVisitorTypesRequest({ ...ListVisitorTypesRequest.defaults })
      EventPointOfInterest.refName
      hasSubmitted.current = false
    }

    if (spacesList.status === Status.Idle) {
      spacesRequest()
    }

    releaseControlsListReleaseControlsRequest([pointOfInterestRef])

    gettyListValidationFailuresRequest({
      ...ListValidationFailuresRequest.defaults,
      page: {
        ...Page.defaults,
        pageSize: 100,
      },
      filter: {
        ...ListValidationFailuresRequest.ByRef.defaults,
        entityRefs: [pointOfInterestRef]
      }
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (initialFormValues && initialFormValues.placement) {
      const space = spacesListData && spacesListData.find(obj => obj && obj.ref && initialFormValues && initialFormValues.placement && obj.ref.id === initialFormValues.placement.id);
      if (space && space.siteRef && space.siteRef.id) {
        setSiteId(space.siteRef.id)
      }
    }
  }, [pointOfInterest, spacesListData]); // eslint-disable-line react-hooks/exhaustive-deps

  const getResolvedFormValues = (keys: ReadonlyArray<keyof EventPointOfInterest.Entity>): { [key: string]: any} => {
    const pointOfInterestValues: Partial<EventPointOfInterest.Entity> = pick({
      ...EventPointOfInterest.defaults,
      ...pointOfInterest
    }, ...keys)
    return convertEntityToFormValues<EventPointOfInterest.Entity>(pointOfInterestValues, eventPointOfInterestSchema)
  }

  const resolveSiteName = useCallback((siteRef: Site.Ref | undefined) => {
    if (!siteRef) {
      return ""
    }

    const site = sitesData && sitesData.sites && sitesData.sites.find((site) => Site.mustRef(site.ref).id === siteRef.id)
    return site ? site.name : ""
  }, [sitesData]);

  useEffect(() => {
    if (!spacesListData) return;

    const formatted = spacesListData.map(item => {
      const value: Placement.Entity = {
        type: 'mos.placement.Placement',
        placement: {
          type: 'mos.placement.SpacePlacement',
          spaceRef: Space.mustRef(item.ref)
        }
      };

      const label = `${resolveSiteName(item.siteRef)} - ${
        item.displayName ? item.displayName : item.name
      }`;

      return { label, value };
    });

    setPlacementOverrideFieldValues(formatted);

  }, [spacesListData, resolveSiteName])

  // setup the initial form values.
  const initialFormValues: FormValues | undefined = pointOfInterest ?
    {
      ...(getResolvedFormValues(['teaserText', 'content', 'additionalInfo', 'deepDiveLabel', 'deepDiveContextLabel', 'deepDiveUrl', 'tags']) as FormValues),
      placement: getSpacePlacementSpaceRef(eventPointOfInterestSchema.placement.read(pointOfInterest.placement) as Placement.Entity)
    }:
    undefined
  const initialAudioValue = pointOfInterest && eventPointOfInterestSchema.audio.read(pointOfInterest.audio) as Media.Ref | undefined;

  useEffect(() => {
    // once the entity is ready, initialise the form values, or reinitialise once an update was
    // successful.
    if (eventPointOfInterestUpdate.status === Status.Ready && initialFormValues) {
      setFormBag(createFormBag(initialFormValues))
      setAudioMedia(initialAudioValue)
    }

    // if the status is ready and the data had been submitted, trigger success toast.
    if (eventPointOfInterestUpdate.status === Status.Ready && hasSubmitted.current) {
      releaseControlsListReleaseControlsRequest([pointOfInterestRef])
      toastNotification({ type: 'success', text: 'Point Of Interest updated' })
      hasSubmitted.current = false
      gettyListValidationFailuresRequest({
        ...ListValidationFailuresRequest.defaults,
        page: {
          ...Page.defaults,
          pageSize: 100,
        },
        filter: {
          ...ListValidationFailuresRequest.ByRef.defaults,
          entityRefs: [pointOfInterestRef]
        }
      })
    }

    // if the status is stale, trigger failure toast.
    if (eventPointOfInterestUpdate.status === Status.Stale) {
      gettyUpdateEventPointOfInterestReset()
      toastNotification({ type: 'error', text: 'Can’t save because of an unknown error.' })
      hasSubmitted.current = false
    }
  }, [ eventPointOfInterestUpdate.status ]) // eslint-disable-line react-hooks/exhaustive-deps

  // handle the form ui.
  const modified = initialFormValues && pointOfInterestData && formBag && (
    !areObjectsEqual(initialFormValues, formBag.values) ||
    !areRefValuesEqual(initialAudioValue, audioMedia)
  )

  const validateForm = (values: FormValues) => {
    const errors: FormErrors<FormValues> = {}

    return errors
  }

  // attempt to submit validated data to the api.
  const submit = (formBag: FormBag<FormValues>, pointOfInterest: EventPointOfInterest.Entity) => {
    const bag = validateFormBag(formBag, validateForm)
    setFormBag(bag)

    if (!bag.valid) {
      return false
    }

    hasSubmitted.current = true
    const values: Omit<FormValues, "placement"> = formBag.values;
    const entityValues = Object.entries(values).reduce((accumulator, currentValue) => {
      const key = currentValue[0] as keyof EventPointOfInterest.Entity
      const value = currentValue[1]
      if (key === "placement") return accumulator
      return {
        ...accumulator,
        [key]: eventPointOfInterestSchema[key] ? eventPointOfInterestSchema[key].write(value, pointOfInterest[key]) : value
      }
    }, {})

    const placement = formBag.values.placement && makePlacementEntity(formBag.values.placement);

    gettyUpdateEventPointOfInterestRequest({
      ...UpdateEventPointOfInterestRequest.defaults,
      eventPointOfInterestRef: EventPointOfInterest.mustRef(pointOfInterest.ref),
      updateMask: {
        type: 'google.protobuf.FieldMask',
        fields: [
          'teaserText',
          'content',
          'additionalInfo',
          'deepDiveLabel',
          'deepDiveContextLabel',
          'deepDiveUrl',
          'tags',
          'audio',
          'placement',
        ] as any,
      },
      ...entityValues,
      audio: writeUsingSchema<EventPointOfInterest.Entity>(eventPointOfInterestSchema, 'audio', audioMedia, pointOfInterest.audio),
      placement: writeUsingSchema<EventPointOfInterest.Entity>(eventPointOfInterestSchema, "placement", placement, pointOfInterest.placement),
    })

    return false
  }

  const releaseControlUpdate = (bundles: ReadonlyArray<SetReleaseStatusRequest.BundleStatus.Entity>) => {
    releaseControlsSetReleaseStatusRequest({
      ...SetReleaseStatusRequest.defaults,
      ref: pointOfInterestRef,
      bundles,
    })
  }

  const failures = validationFailuresListData ? validationFailuresListData.find(({ ref }) => ref && ref.id === pointOfInterestId) : undefined
  const failureFields = failures && failures.issues

  const title = pointOfInterest && pointOfInterest.event ? pointOfInterest.event.title : ''

  return (
    <>
      {deletePointOfInterestModalOpen && (
        <EventPointOfInterestDelete
          historyPush={historyPush}
          projectId={projectId}
          onCancel={() => setDeletePointOfInterestModalOpen(false)}
          pointOfInterest={pointOfInterest}
          moodId={moodId}
        />
      )}
      <LayoutListOrFormPage
        nav={() => (
          <PrimaryNavigationEditEntity
            title="Edit Point of Interest"
            onConfirm={modified ? () => historyPush(backLocation) : undefined}
            navBackRoute={backLocation}
          />
        )}
        title={title}
        toolbar={() => (
          <ButtonGroup>
            <ReleaseControlUpdate
              hasBorder
              entityRef={pointOfInterestRef}
              updateHandler={releaseControlUpdate}
            />
            <PopOverMenu hasBorder>
              <PopOverMenuButton onClick={() => setDeletePointOfInterestModalOpen(true)}>
                Delete
              </PopOverMenuButton>
            </PopOverMenu>
          </ButtonGroup>
        )}
        summary={failureFields ? () => failureFields && <StatusSummary warning={[{ text: `The following fields are required to publish this point of interest: ${failureFields.map(({ attributeDisplayName }) => attributeDisplayName).join(', ') }.` }]} /> : undefined}
        subnav={() => (
          <SecondaryNavigation
            navItems={getEventPointOfInterestSubnav(projectId, pointOfInterestId, moodId)}
            modified={modified}
            historyPush={historyPush}
          />
        )}
        content={() => (
          <StatusGuard status={eventPointOfInterestUpdate} mode="form">
            {formBag && spacesListData && pointOfInterest && (
            <form noValidate onSubmit={(event) => { event.preventDefault() }}>
              <FormData bag={formBag} onUpdate={(event) => { setFormBag(event.bag) }} validate={() => ({})} >
                <Input
                  component="textarea"
                  label="Teaser Text"
                  field="teaserText"
                  warning={getValidationWarning<EventPointOfInterest.Entity>('teaserText', failureFields)}
                />
                <Input
                  component="textarea"
                  label="Content"
                  field="content"
                  warning={getValidationWarning<EventPointOfInterest.Entity>('content', failureFields)}
                />
                <Input
                  component="textarea"
                  label="Additional Info"
                  field="additionalInfo"
                  warning={getValidationWarning<EventPointOfInterest.Entity>('additionalInfo', failureFields)}
                />
                <Input
                  label="Deep Dive Label"
                  field="deepDiveLabel"
                  warning={getValidationWarning<EventPointOfInterest.Entity>('deepDiveLabel', failureFields)}
                />
                <Input
                  label="Deep Dive Context Label"
                  field="deepDiveContextLabel"
                  warning={getValidationWarning<EventPointOfInterest.Entity>('deepDiveContextLabel', failureFields)}
                />
                <Input
                  label="Deep Dive URL"
                  field="deepDiveUrl"
                  warning={getValidationWarning<EventPointOfInterest.Entity>('deepDiveUrl', failureFields)}
                />
                <MediaField
                  kind="MEDIA_KIND_AUDIO"
                  mediaRef={audioMedia}
                  onUpdate={(entity) => { setAudioMedia(entity ? entity.ref : undefined) }}
                  label="Audio"
                  warning={getValidationWarning<EventPointOfInterest.Entity>('audio', failureFields)}
                  />
                <SelectRef<FormValues, "placement">
                  label="Location"
                  field="placement"
                  isClearable
                  options={spacesListData.map((item) => ({
                    label: `${resolveSiteName(item.siteRef)} - ${item.displayName ? item.displayName : item.name}`,
                    value: Space.mustRef(item.ref)
                  }))}
                  warning={getValidationWarning<EventPointOfInterest.Entity>('placement', failureFields)}
                />
                <Input
                  label="Visitor Type Tags"
                  field="tags"
                  warning={getValidationWarning<EventPointOfInterest.Entity>('tags', failureFields) || visitorTypeHelpText(visitorTypesData)}
                />
              </FormData>
            </form>
              )}
            <section>
              <h4>Deep Link</h4>
              <a href={deepLinkUrl} target="_blank" rel="noopener noreferrer">
                {deepLinkUrl}
              </a>
            </section>
          </StatusGuard>
        )}
        sidebar={() => (
          <></>
        )}
        footer={() => formBag && pointOfInterest && (
          <FooterEditEntity
            entity={eventPointOfInterestUpdate}
            modified={modified}
            onCancel={() => historyPush(backLocation)}
            onSave={() => {
              submit(formBag, pointOfInterest)
              return false
            }}
          />
        )}
      />
    </>
  )
}

export const EventPointOfInterestUpdate = connect<ConnectedProps, ActionProps, DirectProps, GettyStore, GettyActionCreators>(
  (store) => ({
    ...pick(store.getty, "eventPointOfInterestUpdate", "sites", "validationFailuresList", "visitorTypes"),
    ...pick(store.spaces, "spacesList"),
  }),
  {
    ...pick(actionCreators, 'gettyGetEventPointOfInterestRequest', 'gettyUpdateEventPointOfInterestRequest', 'gettyUpdateEventPointOfInterestReset', 'gettyListSitesRequest', 'gettyListSitesRequest', 'gettyListValidationFailuresRequest', 'gettyListVisitorTypesRequest'),
    ...pick(releaseControlsBindableActionCreators, 'releaseControlsListReleaseControlsRequest', 'releaseControlsSetReleaseStatusRequest'),
    ...pick(spacesBindableActionCreators, "spacesRequest"),
    ...pick(sharedActionCreators, 'toastNotification'),
  }
)(EventPointOfInterestUpdateView)
